import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { PolarAngleAxis, PolarGrid, Radar, RadarChart } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { BUSINESS_PAINS } from "../../types/use-case.types"

type BusinessPainRadarProps = {
  useCases: DashboardUseCase[]
}

export function BusinessPainRadar({ useCases }: BusinessPainRadarProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        count: { label: t("useCases.dashboard.charts.useCases"), color: "hsl(var(--chart-1))" },
      }) satisfies ChartConfig,
    [t]
  )

  const data = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const pain of BUSINESS_PAINS) {
      counts[pain] = 0
    }
    for (const uc of useCases) {
      for (const val of uc.bpSel) {
        if (val in counts) counts[val]++
      }
    }
    return BUSINESS_PAINS.map((pain) => ({
      subject: pain,
      count: counts[pain],
    }))
  }, [useCases])

  if (useCases.length === 0) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.businessPainRadar")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer className="h-[280px] w-full" config={chartConfig}>
          <RadarChart cx="50%" cy="50%" data={data} outerRadius="70%">
            <PolarGrid />
            <PolarAngleAxis
              className="text-muted-foreground"
              dataKey="subject"
              tick={{ fontSize: 11, fill: "currentColor" }}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Radar
              dataKey="count"
              fill="hsl(var(--chart-1))"
              fillOpacity={0.5}
              name={t("useCases.dashboard.charts.useCases")}
              stroke="hsl(var(--foreground))"
            />
          </RadarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
